import { Loader, Link, Button, IconButton } from '@deque/cauldron-react';
import { filterUserFlowResults } from '@deque/axe-devtools-issues-utils';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import PageTitle from '../../common/components/PageTitle';
import CopyToClipboard from '../../common/components/CopyToClipboard';
import { useAuthContext } from '../../common/contexts/auth';
import useIssuesFilter from '../../common/hooks/useIssuesFilter';
import { useUserFlow } from '../contexts/UserFlow';
import styles from './Test.css';
import useMediaQuery from '../../common/hooks/useMediaQuery';
import { isNotFoundError } from '../../common/utils/errors';
import useUserFlowAnalyticsData from '../hooks/useUserFlowAnalyticsData';
import useSharedWith from '../hooks/useSharedWith';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import UserFlowDetails from '../components/UserFlowDetails';
import FeedbackToast from '../../common/components/FeedbackToast';
import ScrimmedLoader from '../../common/components/ScrimmedLoader';
import SimpleExportIssuesModal from '../components/SimpleExportIssueModal';

const UserFlowPage: React.FC = () => {
  const { login } = useAuthContext();
  const { loading, error, userFlow, results, updateUserFlow, updating } =
    useUserFlow();

  const { filters } = useIssuesFilter([]);
  const filteredResults = results && filterUserFlowResults(results, filters);
  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 45rem)');
  const analytics = useAxeDevtoolsProAnalytics();
  const loaderRef = useRef<HTMLDivElement>(null);

  const { ready: analyticsReady, data: analyticsData } =
    useUserFlowAnalyticsData();
  const { ready: sharedWithReady, sharedWith } = useSharedWith();

  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    if (analyticsReady) {
      analytics.viewUfaReport(analyticsData);
    }
  }, [analyticsReady]);

  useEffect(() => {
    if (!updating) {
      return;
    }

    loaderRef.current?.focus();
  }, [updating, loaderRef.current]);

  const onCopyShare = async () => {
    if (analyticsReady) {
      analytics.ufaReportCopyUrlClick(analyticsData);
    }

    if (!userFlow || userFlow.shared_with) {
      return;
    }

    if (sharedWithReady) {
      await updateUserFlow({
        shared_with: sharedWith
      });
    }
  };

  if (loading) {
    return (
      <>
        <PageTitle title={t('Loading user flow')} />
        <Loader label={t('Loading user flow...')} />
      </>
    );
  }

  const handleLogin = (): void => {
    login();
  };

  if (error && (isNotFoundError(error) || error.status === 401)) {
    return (
      <div className={styles.wrap}>
        <PageTitle title={t('User flow not found')} />
        <div className={styles.header}>
          <h1>{t('User flow not found')}</h1>
        </div>
        <p>
          <Trans>We were unable to find this user flow.</Trans>{' '}
          <Trans>
            You may need to{' '}
            <Button onClick={handleLogin} variant="link">
              sign in
            </Button>{' '}
            or ensure you have access to this user flow.
          </Trans>
        </p>
      </div>
    );
  } else if (error || !userFlow || !filteredResults || !results) {
    return (
      <div className={styles.wrap}>
        <PageTitle title={t('Failed to load user flow')} />
        <div className={styles.header}>
          <h1>{t('Failed to load user flow')}</h1>
        </div>
        <p>
          <Trans>We were unable to load the user flow.</Trans>{' '}
          <Trans>
            Please try again, and if the issue persists, contact{' '}
            <Link href="mailto:helpdesk@deque.com">helpdesk@deque.com</Link>.
          </Trans>
        </p>
      </div>
    );
  }

  const userFlowName = userFlow.name || t('User Flow');
  const pageTitle = t('View {{userFlowName}}', { userFlowName });
  const testLink = `${window.location.origin}${window.location.pathname}`;
  const copyButtonLabel = t('Copy link for {{userFlowName}} to clipboard', {
    userFlowName
  });
  const shareButtonLabel = t(
    'Share user flow {{userFlowName}} and copy link to clipboard',
    {
      userFlowName
    }
  );

  const handleExportClick = () => setShowExportModal(true);

  return (
    <>
      {updating && (
        <ScrimmedLoader
          label={t('Sharing your User Flow analyzer report...')}
          ref={loaderRef}
        />
      )}
      <div className={styles.banner}>
        <FeedbackToast
          text="This feature is in beta. We would appreciate your feedback to improve the experience!"
          surveyLink="https://www.surveymonkey.com/r/8SB9DXP"
        />
      </div>
      <div className={styles.wrap}>
        <PageTitle title={pageTitle} />
        <div className={styles.header}>
          <h1>{pageTitle}</h1>
          <CopyToClipboard
            onCopy={onCopyShare}
            text={testLink}
            confirmationMessage={t(
              'The User Flow analyzer report link has been copied to your clipboard'
            )}
            errorMessage={t('Failed to share your User Flow analyzer report')}
          >
            <IconButton
              icon={userFlow.shared_with ? 'link' : 'share-nodes'}
              label={userFlow.shared_with ? copyButtonLabel : shareButtonLabel}
              tooltipProps={{
                placement: 'bottom'
              }}
            />
          </CopyToClipboard>

          <IconButton
            icon="download"
            label={t('Export saved test and issues for {{userFlowName}}', {
              userFlowName
            })}
            onClick={handleExportClick}
          />
        </div>
        <UserFlowDetails
          results={filteredResults}
          filters={filters}
          narrow={narrow}
        />

        {showExportModal && (
          <SimpleExportIssuesModal
            exportEntireFlow
            userFlow={userFlow}
            userFlowResults={results}
            onClose={() => setShowExportModal(false)}
          />
        )}
      </div>
    </>
  );
};

export default UserFlowPage;
